import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';
import { DateTimePicker } from "@material-ui/pickers";

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Checkbox from '@material-ui/core/Checkbox';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Alert from '@material-ui/lab/Alert';

import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';

import RedButton from 'app/components/buttons/RedButton';
import Loading from 'app/components/loaders/Loading';
import useApi from 'app/context/useApi';

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        margin="normal"
        {...props.inputProps}
    />
);

const useListItemStyles = makeStyles(theme => ({
    nested: props => ({
        paddingLeft: (10 + props.depth * 14),
    }),
    listItemIcon: {
        minWidth: 0
    }
}));

const findChildIds = (children) => {

    let ids = [];

    children.forEach(c => {

        ids.push(c.id);

        if (c.children_with_count.length > 0) {
            ids = ids.concat(findChildIds(c.children_with_count));
        }
    })

    return ids;
}

const NestedLevelItem = (props) => {

    const { level, depth, levelsChecked, onCheck, onCheckChildren } = props;

    const classes = useListItemStyles({ depth: depth});

    const [ includeChildren, setIncludeChildren ] = useState(false);

    const isSelected = levelsChecked.indexOf(level.id) > -1 ;

    const childIds = (level.children_with_count.length === 0) ? 0 : useMemo(() => findChildIds(level.children_with_count), []);

    const handleIncludeChildren = () => {
        setIncludeChildren(! includeChildren);
        const ids = [...childIds];
        if (! includeChildren) ids.push(level.id);
        onCheckChildren(! includeChildren, ids);
    }

    return (
        <>
            <ListItem
                classes={{ root: classes.nested }}
                button
                dense
                onClick={() => onCheck(level.id)}
                >
                <ListItemIcon classes={{ root: classes.listItemIcon }}>
                    <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        color="primary"
                    />
                </ListItemIcon>
                <ListItemText
                    primary={ level.title }
                    className={ classes.listItemText }
                />
                <ListItemSecondaryAction>
                    <Typography
                        display="inline"
                        color="textSecondary"
                        variant="caption"
                        >
                        Incl. Sub
                    </Typography>
                    <Switch
                        edge="end"
                        onChange={handleIncludeChildren}
                        checked={includeChildren}
                        disabled={level.children_with_count.length === 0}
                        color="primary"
                    />
                </ListItemSecondaryAction>
            </ListItem>
            {level.children_with_count.map((child) => (
                <NestedLevelItem
                    key={child.id}
                    level={child}
                    depth={(depth + 1)}
                    levelsChecked={levelsChecked}
                    onCheck={onCheck}
                    onCheckChildren={onCheckChildren}
                />
            ))}
        </>
    )
}

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    margin: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    marginRight: {
        marginRight: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    options: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3)
    },
    option: {
        marginBottom: theme.spacing(1)
    },
    listItemIcon: {
        minWidth: 0
    },
    listItem: {
        paddingLeft: 24
    },
    scroll: {
        maxHeight: 400,
        overflowY: 'scroll',
        backgroundColor: '#eee',
        marginTop: theme.spacing(3)
    },
    noContentPadding: {
        padding: 0
    },
}));

const initTicket = {
    id: 0,
    type: 'both',
    description: '',
    title: '',
    event_id: 0,
    price: 0,
    late_by: null,
    late_price: 0,
    amount: 0,
    pay_later: false,
    purchase_by: null,
    dates_allowed: 0,
    variants: []
};

const ticketGroups = [
    {
        title: "Everyone",
        id: 'both'
    },
    {
        title: "Event Group Only",
        id: 'group'
    },
    {
        title: "Guests Only",
        id: 'guest'
    },
    {
        title: "Admin Only",
        id: 'admin'
    }
];

export default function EditTicketDialog(props) {

    const {
        open,
        ticket,
        event,
        onClose,
        onUpdate,
        onClosed,
    } = props;

    const classes = useStyles();
    const api = useApi();

    const [ values, setValues ] = useState(initTicket);
    const [ groupsChecked, setGroupsChecked ] = useState([]);

    const [ groups, setGroups ] = useState([]);
    const [ levels, setLevels ] = useState([]);

    const [ loading, setLoading ] = useState(false);
    const [ groupsLoading, setGroupsLoading ] = useState(false);
    const [ levelsLoading, setLevelsLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const getGroups = () => {
        setGroupsLoading(true);

        api.get('/admin/group/group')
            .then(res => setGroups(res.groups))
            .finally(() => setGroupsLoading(false));
    }

    const getLevels = () => {
        setLevelsLoading(true);

        api.get('/admin/group/level/recursive/0')
            .then(res => setLevels(res.groups))
            .finally(() => setLevelsLoading(false));
    }

    useEffect(() => {
        getGroups();
        getLevels();
    }, [])

    useEffect(() => {
        if (ticket && typeof ticket === 'object') {
            const tempTicket = {...ticket};

            if (tempTicket.late_by) tempTicket.late_by = DateTime.fromISO(tempTicket.late_by);
            if (tempTicket.purchase_by) tempTicket.purchase_by = DateTime.fromISO(tempTicket.purchase_by);
            if (tempTicket.type === null) tempTicket.type = 2;

            setGroupsChecked(ticket.groups.map(g => g.id));
            setValues(tempTicket);
        } else {
            setValues({...initTicket, dates_allowed: event.dates_allowed});
            setGroupsChecked([]);
        }
        setErrors({});
    }, [ticket]);

    // Setter Functions ------------------------------------------

    const handleTicket = (name, event) => {

        let value;

        if (DateTime.isDateTime(event) || event === null) {
            value = event;
        } else {

            const target = event.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        }

        removeError(name);

        setValues({...values, [name]: value});
    };

    const addVariant = () => {

        const newVariants = [...values.variants];

        newVariants.push('');

        setValues({...values, variants: newVariants});
    }

    const removeVariant = (key) => {

        const newVariants = [...values.variants];

        newVariants.splice(key, 1);

        setValues({...values, variants: newVariants});
    }

    const handleVariant = (event, i) => {

        const value = event.target.value;

        const newVariants = [...values.variants];

        newVariants[i] = value;

        setValues({...values, variants: newVariants});
    }

    const handleGroup = id => {
        const newChecked = [...groupsChecked]
        const i = newChecked.indexOf(id)

        removeError('groups');

        if (i > -1) {
            newChecked.splice(i, 1)
        } else {
            newChecked.push(id)
        }

        setGroupsChecked(newChecked);
    }

    const handleLevelChildren = (addChildren, ids) => {
        let newChecked = [...groupsChecked];

        removeError('groups');

        if (addChildren) {
            newChecked = [...new Set(newChecked.concat(ids))];
        } else {
            newChecked = newChecked.filter((el) => !ids.includes(el));
        }

        setGroupsChecked(newChecked);
    }

    // Post functions ------------------------------------------

    const postTicket = () => {

        const data = {...values};

        data.event_id = event.id
        data.groups = [...groupsChecked];

        if (values.late_by) data.late_by = values.late_by.toSeconds();
        if (values.purchase_by) data.purchase_by = values.purchase_by.toSeconds();
        if (data.type === 2) data.type = null;

        api.post(`/admin/ticket/${values.id}`, data)
            .then(() => onUpdate())
            .catch(error => {
                setLoading(false);

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    }

    const deleteTicket = () => {

        setLoading(true);

        api.delete(`/admin/ticket/${values.id}`)
            .then(() => onUpdate())
            .catch(() => setLoading(false));
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const removeError = field => {
        const newErrors = {...errors};

        delete newErrors[field];

        setErrors(newErrors);
    }

    const getDatesAllowedText = () => {
        if (isError('dates_allowed')) return getErrorMessage('dates_allowed');
        return "Allows ticket purchaser to add dates to their invitation. Overrides the number of dates allowed for the entire event."
    }

    const getAmountMessage = () => {
        if (isError('amount')) return errors.amount;
        return "The total amount of this ticket that are available for purchase. Leave 0 for no limit on amount."
    }

    const getLateByMessage = () => {
        if (isError('late_by')) return errors.late_by;
        return "Leave blank if you wish to not raise the price by a specific date and time."
    }

    const getPurchaseByMessage = () => {
        if (isError('purchase_by')) return errors.purchase_by;
        return "Leave blank if you wish to allow ticket sales up to and through the event."
    }

    const clearState = () => {
        setValues(initTicket);
    };

    const onExited = () => {
        clearState();
        onClosed();
        setLoading(false);
    }

    return (
        <Dialog
            aria-labelledby="edit-ticket-dialog-title"
            open={open}
            onClose={onClose}
            aria-describedby="edit-ticket-dialogue-description"
            fullWidth
            maxWidth="md"
            onExited={onExited}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="edit-ticket-dialog-title">
                <span>{(values.id > 0) ? "Edit " + values.title : "New Ticket"}</span>
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="h6" className={classes.marginBottom}>Basic Information</Typography>
                <TextField
                    id="ticket-title"
                    label="Title"
                    value={values.title}
                    onChange={event => handleTicket('title', event)}
                    variant="outlined"
                    fullWidth
                    placeholder="ie. General Admission"
                    error={isError('title')}
                    helperText={getErrorMessage('title')}
                    margin="normal"
                />
                <TextField
                    id="ticket-description"
                    value={values.description}
                    label='Ticket Description'
                    onChange={event => handleTicket('description', event)}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    error={isError('description')}
                    helperText={getErrorMessage('description')}
                    margin="normal"
                />
                <TextField
                    id="select-type"
                    select
                    label="Select Ticket Type"
                    value={values.type}
                    onChange={e => handleTicket('type', e)}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    error={isError('type')}
                    helperText={getErrorMessage('type')}
                    >
                    {ticketGroups.map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                            {group.title}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Number of Dates Allowed per Ticket?"
                    id="dates-allowed"
                    type="number"
                    value={values.dates_allowed}
                    onChange={e => handleTicket('dates_allowed', e)}
                    variant="outlined"
                    fullWidth
                    error={isError('dates_allowed')}
                    margin="normal"
                    helperText={getDatesAllowedText()}
                />
                <DateTimePicker
                    value={values.purchase_by}
                    inputVariant="outlined"
                    onChange={e => handleTicket('purchase_by', e)}
                    variant="outlined"
                    TextFieldComponent={renderDateInput}
                    minutesStep={15}
                    clearable
                    error={isError('purchase_by')}
                    helperText={getPurchaseByMessage()}
                    label="Date ticket sales should be disabled"
                    className={classes.marginBottom}
                />
                <Typography variant="h6" className={classes.marginBottom}>Pricing Information</Typography>
                <Grid container spacing={3} className={classes.marginBottom}>
                    <Grid item xs={6}>
                        <TextField
                            id="ticket-amount"
                            value={values.amount}
                            label='Number of Ticket Available'
                            onChange={event => handleTicket('amount', event)}
                            variant="outlined"
                            fullWidth
                            type='number'
                            error={isError('amount')}
                            helperText={getAmountMessage()}
                            margin="normal"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            id="ticket-price"
                            label="Price Per Ticket"
                            variant="outlined"
                            value={values.price}
                            onChange={event => handleTicket('price', event)}
                            fullWidth
                            type='number'
                            error={isError('price')}
                            helperText={getErrorMessage('price')}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                inputProps: {
                                    min: 0,
                                    step: 0.01
                                }
                            }}
                            margin="normal"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DateTimePicker
                            value={values.late_by}
                            inputVariant="outlined"
                            onChange={e => handleTicket('late_by', e)}
                            variant="outlined"
                            TextFieldComponent={renderDateInput}
                            minutesStep={15}
                            clearable
                            error={isError('late_by')}
                            helperText={getLateByMessage()}
                            label="Date of Price Increase"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            id="ticket-late-price"
                            label="Late Purchase Price Per Ticket"
                            variant="outlined"
                            value={values.late_price}
                            onChange={e => handleTicket('late_price', e)}
                            fullWidth
                            type='number'
                            margin="normal"
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                inputProps: {
                                    min: 0,
                                    step: 0.01
                                }
                            }}
                        />
                    </Grid>
                </Grid>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values.pay_later}
                            onChange={e => handleTicket('pay_later', e)}
                            color="primary"
                            name="pay_later"
                        />
                    }
                    label="Allow purchaser to pay later (ie. by check or cash)?"
                    labelPlacement="end"
                    margin="normal"
                    className={classes.marginBottom}
                />
                <Typography variant="h6" gutterBottom>Advanced Information</Typography>
                <div className={classes.options}>
                    <Typography
                        variant='subtitle1'
                        gutterBottom
                        >
                        Ticket Variants
                    </Typography>
                    {values.variants.map((o, i) => (
                        <div>
                            <FormControl className={classes.option} variant="outlined" key={i}>
                                <OutlinedInput
                                    key={i}
                                    value={o}
                                    placeholder="Variant Name"
                                    onChange={event => handleVariant(event, i)}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="remove variant"
                                                onClick={() => removeVariant(i)}
                                                >
                                                <DeleteIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                        </div>
                    ))}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={addVariant}
                        >
                        Add Variant
                    </Button>
                </div>
                <div className={classes.options}>
                    <Loading isLoading={(groupsLoading || levelsLoading)}/>
                    <Typography
                        variant='subtitle1'
                        >
                        Ticket Groups
                    </Typography>
                    <Typography
                        variant='body2'
                        color="textSecondary"
                        >
                        Groups who can issue this Ticket to a Guest
                    </Typography>
                    <div className={classes.scroll}>
                        <List className={classes.noContentPadding}>
                            {groups.map(group => (
                                <ListItem
                                    classes={{ root: classes.listItem }}
                                    button
                                    dense
                                    onClick={() => handleGroup(group.id)}
                                    key={'group' + group.id}
                                    >
                                    <ListItemIcon classes={{ root: classes.listItemIcon }}>
                                        <Checkbox
                                            edge="start"
                                            checked={(groupsChecked.indexOf(group.id) > -1)}
                                            tabIndex={-1}
                                            disableRipple
                                            color="primary"
                                        />
                                    </ListItemIcon>
                                    <ListItemText primary={group.title}/>
                                </ListItem>
                            ))}
                            {levels.map(level => (
                                <NestedLevelItem
                                    key={level.id}
                                    level={level}
                                    depth={1}
                                    levelsChecked={groupsChecked}
                                    onCheck={handleGroup}
                                    onCheckChildren={handleLevelChildren}
                                />
                            ))}
                        </List>
                    </div>
                    {isError('groups') &&
                        <Alert className={classes.marginTop}  severity="error">{getErrorMessage('groups')}</Alert>
                    }
                </div>
            </DialogContent>
            <DialogActions>
                { (ticket && ticket.id > 0) &&
                    <RedButton
                        onClick={deleteTicket}
                        className={classes.marginRight}
                        disabled={loading}
                        >
                        Delete
                    </RedButton>
                }
                <Button
                    onClick={postTicket}
                    color="primary"
                    variant="contained"
                    disabled={loading}
                    >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
